import { useState } from 'react';
import { Check, Search, X } from 'lucide-react';
import { flagFor } from '@/utils/countryFlag';

/**
 * Shared country picker for the landing pages and the subscription page, so a choice
 * made in one is the choice in the other. Display only — it selects which price list to show.
 */
export const CountrySheet = ({ open, onClose, countries, selected, onSelect, title = 'Choose your country' }) => {
  const [search, setSearch] = useState('');
  if (!open) return null;

  const term = search.trim().toLowerCase();
  const list = term
    ? countries.filter(c => c.name.toLowerCase().includes(term) || c.code.toLowerCase() === term)
    : countries;

  const pick = (code) => { onSelect(code); setSearch(''); onClose(); };

  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center sm:items-center bg-black/80 backdrop-blur-sm"
         onClick={onClose} data-testid="country-sheet">
      <div className="w-full sm:max-w-md max-h-[80vh] flex flex-col rounded-t-3xl sm:rounded-3xl border border-white/10 bg-[#101010]"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-3">
          <h3 className="font-heading font-black uppercase tracking-wider text-white text-lg">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="h-8 w-8 rounded-full bg-white/5 text-white/60 flex items-center justify-center"
                  data-testid="country-sheet-close">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pb-3">
          <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/40 px-3">
            <Search size={15} className="text-white/35" />
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search countries"
              className="flex-1 bg-transparent py-2.5 text-sm text-white outline-none placeholder:text-white/30"
              data-testid="country-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 pb-6">
          {list.map(c => (
            <button
              key={c.code}
              onClick={() => pick(c.code)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left ${
                c.code === selected ? 'bg-white/[0.07]' : 'hover:bg-white/[0.04]'
              }`}
              data-testid={`country-option-${c.code}`}
            >
              <span className="text-xl leading-none" aria-hidden="true">{flagFor(c.code)}</span>
              <span className="flex-1 text-sm text-white truncate">{c.name}</span>
              <span className="text-xs text-white/35">{c.currency}</span>
              {c.code === selected && <Check size={15} className="text-accent shrink-0" />}
            </button>
          ))}
          {!list.length && <p className="px-3 py-6 text-center text-sm text-white/40">No match</p>}
        </div>
      </div>
    </div>
  );
};

export default CountrySheet;
